'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { css, cx } from '@/styled-system/css';

const elasticContainerStyle = css({
  display: 'inline-block',
  position: 'relative',
  verticalAlign: 'bottom',
  overflow: 'hidden',
  // Keep the width-animation clip while leaving room for glyph descenders.
  paddingBlockEnd: 'tightest',
  marginBlockEnd: 'calc({spacing.tightest} * -1)',
  transitionProperty: 'width',
  transitionDuration: '0.5s',
  transitionTimingFunction: 'cubic-bezier(0.25, 0.1, 0.25, 1)',
});

const elasticWordStyle = css({
  display: 'inline-block',
  whiteSpace: 'nowrap',
  transitionProperty: 'filter, opacity, transform',
  transitionTimingFunction: 'cubic-bezier(0.25, 0.1, 0.25, 1)',
});

type ElasticBlurWordProps = {
  words: Array<string>;
  interval?: number;
  className?: string;
};

const BLUR_OUT_MS = 350;
const BLUR_IN_MS = 400;

type Phase = 'visible' | 'blurring-out' | 'swapping' | 'blurring-in';

export function ElasticBlurWord({ words, interval = 3000, className }: ElasticBlurWordProps) {
  const [wordIndex, setWordIndex] = useState(0);
  const [phase, setPhase] = useState<Phase>('visible');
  const [containerWidth, setContainerWidth] = useState<number | undefined>(undefined);
  const [widths, setWidths] = useState<Array<number>>([]);
  const wordRef = useRef<HTMLSpanElement>(null);
  const cycleTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const hasRotated = useRef(false);

  const prefersReducedMotion =
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const wordIndexRef = useRef(wordIndex);
  wordIndexRef.current = wordIndex;

  const widthsRef = useRef(widths);
  widthsRef.current = widths;

  const measureWidths = useCallback(() => {
    if (!wordRef.current) return;

    const style = window.getComputedStyle(wordRef.current);
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return;

    context.font =
      style.font ||
      `${style.fontStyle} ${style.fontVariant} ${style.fontWeight} ${style.fontSize} / ${style.lineHeight} ${style.fontFamily}`;

    const letterSpacing = Number.parseFloat(style.letterSpacing);
    const measured = words.map((word) => {
      const spacing = Number.isFinite(letterSpacing) ? Math.max(word.length - 1, 0) * letterSpacing : 0;
      return Math.ceil(context.measureText(word).width + spacing) + 2;
    });

    setWidths(measured);
  }, [words]);

  useEffect(() => {
    measureWidths();
    document.fonts?.ready.then(measureWidths);
    window.addEventListener('resize', measureWidths);

    return () => {
      window.removeEventListener('resize', measureWidths);
    };
  }, [measureWidths]);

  useEffect(() => {
    if (!hasRotated.current) return;

    const width = widths[wordIndex];
    if (width != null) {
      setContainerWidth(width);
    }
  }, [widths, wordIndex]);

  // Reduced motion: just swap words on interval, no animation
  useEffect(() => {
    if (!prefersReducedMotion) return;
    const id = setInterval(() => {
      setWordIndex((prev) => {
        const next = (prev + 1) % words.length;
        if (widthsRef.current.length > 0) {
          setContainerWidth(widthsRef.current[next]);
        }
        return next;
      });
    }, interval);
    return () => clearInterval(id);
  }, [prefersReducedMotion, interval, words.length]);

  // Animation cycle — only starts when phase is 'visible'
  useEffect(() => {
    if (prefersReducedMotion || widths.length === 0 || phase !== 'visible') return;

    cycleTimerRef.current = setTimeout(() => {
      // Phase 1: blur out and lock the current width so the next width can animate.
      if (!hasRotated.current) {
        hasRotated.current = true;
        setContainerWidth(wordRef.current?.getBoundingClientRect().width ?? widths[wordIndexRef.current]);
      }
      setPhase('blurring-out');

      setTimeout(() => {
        // Phase 2: swap word + resize container
        setWordIndex((prev) => {
          const nextIndex = (prev + 1) % words.length;
          setContainerWidth(widthsRef.current[nextIndex] ?? widths[nextIndex]);
          return nextIndex;
        });
        setPhase('swapping');

        // Phase 3: blur in after the 0ms swap transition takes effect.
        requestAnimationFrame(() => {
          setPhase('blurring-in');

          setTimeout(() => {
            setPhase('visible');
          }, BLUR_IN_MS);
        });
      }, BLUR_OUT_MS);
    }, interval);

    return () => {
      clearTimeout(cycleTimerRef.current);
    };
  }, [phase, widths, interval, words.length, prefersReducedMotion]);

  const wordStyle = (() => {
    switch (phase) {
      case 'blurring-out':
        return {
          filter: 'blur(10px)',

          opacity: 0,
          transform: 'translateY(-0.18em) scale(0.96)',
          transitionDuration: `${BLUR_OUT_MS}ms`,
        };
      case 'swapping':
        return {
          filter: 'blur(10px)',

          opacity: 0,
          transform: 'translateY(0.18em) scale(1.03)',
          transitionDuration: '0ms',
        };
      case 'blurring-in':
        return {
          filter: 'blur(0)',

          opacity: 1,
          transform: 'translateY(0) scale(1)',
          transitionDuration: `${BLUR_IN_MS}ms`,
        };
      default:
        return {
          filter: 'blur(0)',

          opacity: 1,
          transform: 'translateY(0) scale(1)',
          transitionDuration: `${BLUR_IN_MS}ms`,
        };
    }
  })();

  return (
    <span className={elasticContainerStyle} style={containerWidth != null ? { width: containerWidth } : undefined}>
      <span ref={wordRef} className={cx(className, elasticWordStyle)} style={wordStyle}>
        {words[wordIndex]}
      </span>
    </span>
  );
}
